import { useState, useEffect } from "react";
import { Plus, Trash2, Edit2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { RouterOutputs } from "../../trpc";
import TicketPositionBlock from "./TicketPositionBlock";

type PipelineFull = RouterOutputs["pipeline"]["listFull"][number];
type PipelinePosition = NonNullable<PipelineFull["positions"]["new"]>[number];

const STAGE_LABELS: Record<string, string> = {
  new: "Nuevo",
  pending: "Pendiente",
  success: "Éxito",
  failure: "Fallido",
};

const STAGE_ORDER = ["new", "pending", "success", "failure"] as const;

type FollowUpItem = {
  minutesAfterInactivity: number;
  message: string;
};

type FollowUpConfigFormProps = {
  leadInactivityHours: number | undefined;
  followUpConfig: PipelineFull["followUpConfig"];
  onLeadInactivityHoursChange: (value: number | undefined) => void;
  onFollowUpConfigChange: (next: NonNullable<PipelineFull["followUpConfig"]>) => void;
  isEditing: boolean;
};

const buildFollowUpConfig = (
  cfg: NonNullable<PipelineFull["followUpConfig"]>,
  firstEnabled: boolean,
  secondEnabled: boolean,
  thirdEnabled: boolean,
  first: FollowUpItem,
  second: FollowUpItem,
  third: FollowUpItem
): NonNullable<PipelineFull["followUpConfig"]> => {
  const next = { ...cfg };
  if (firstEnabled) next.firstFollowUp = first;
  else delete (next as Record<string, unknown>).firstFollowUp;
  if (secondEnabled) next.secondFollowUp = second;
  else delete (next as Record<string, unknown>).secondFollowUp;
  if (thirdEnabled) next.thirdFollowUp = third;
  else delete (next as Record<string, unknown>).thirdFollowUp;
  return next;
};

const FollowUpConfigForm = ({
  leadInactivityHours,
  followUpConfig,
  onLeadInactivityHoursChange,
  onFollowUpConfigChange,
  isEditing,
}: FollowUpConfigFormProps) => {
  const cfg = followUpConfig ?? {};
  const [first, setFirst] = useState<FollowUpItem>({
    minutesAfterInactivity: cfg.firstFollowUp?.minutesAfterInactivity ?? 60,
    message: cfg.firstFollowUp?.message ?? "",
  });
  const [second, setSecond] = useState<FollowUpItem>({
    minutesAfterInactivity: cfg.secondFollowUp?.minutesAfterInactivity ?? 120,
    message: cfg.secondFollowUp?.message ?? "",
  });
  const [third, setThird] = useState<FollowUpItem>({
    minutesAfterInactivity: cfg.thirdFollowUp?.minutesAfterInactivity ?? 180,
    message: cfg.thirdFollowUp?.message ?? "",
  });
  const [firstEnabled, setFirstEnabled] = useState(!!cfg.firstFollowUp);
  const [secondEnabled, setSecondEnabled] = useState(!!cfg.secondFollowUp);
  const [thirdEnabled, setThirdEnabled] = useState(!!cfg.thirdFollowUp);

  useEffect(() => {
    setFirst({
      minutesAfterInactivity: cfg.firstFollowUp?.minutesAfterInactivity ?? 60,
      message: cfg.firstFollowUp?.message ?? "",
    });
    setSecond({
      minutesAfterInactivity: cfg.secondFollowUp?.minutesAfterInactivity ?? 120,
      message: cfg.secondFollowUp?.message ?? "",
    });
    setThird({
      minutesAfterInactivity: cfg.thirdFollowUp?.minutesAfterInactivity ?? 180,
      message: cfg.thirdFollowUp?.message ?? "",
    });
    setFirstEnabled(!!cfg.firstFollowUp);
    setSecondEnabled(!!cfg.secondFollowUp);
    setThirdEnabled(!!cfg.thirdFollowUp);
  }, [
    cfg.firstFollowUp?.minutesAfterInactivity,
    cfg.firstFollowUp?.message,
    cfg.secondFollowUp?.minutesAfterInactivity,
    cfg.secondFollowUp?.message,
    cfg.thirdFollowUp?.minutesAfterInactivity,
    cfg.thirdFollowUp?.message,
    cfg.firstFollowUp,
    cfg.secondFollowUp,
    cfg.thirdFollowUp,
  ]);

  const notifyConfig = (
    f: FollowUpItem,
    s: FollowUpItem,
    t: FollowUpItem,
    fe: boolean,
    se: boolean,
    te: boolean
  ) => {
    onFollowUpConfigChange(buildFollowUpConfig(cfg, fe, se, te, f, s, t));
  };

  const handleFirstChange = (next: FollowUpItem) => {
    setFirst(next);
    notifyConfig(next, second, third, firstEnabled, secondEnabled, thirdEnabled);
  };
  const handleSecondChange = (next: FollowUpItem) => {
    setSecond(next);
    notifyConfig(first, next, third, firstEnabled, secondEnabled, thirdEnabled);
  };
  const handleThirdChange = (next: FollowUpItem) => {
    setThird(next);
    notifyConfig(first, second, next, firstEnabled, secondEnabled, thirdEnabled);
  };

  const handleFirstEnabledChange = (enabled: boolean) => {
    setFirstEnabled(enabled);
    notifyConfig(first, second, third, enabled, secondEnabled, thirdEnabled);
  };
  const handleSecondEnabledChange = (enabled: boolean) => {
    setSecondEnabled(enabled);
    notifyConfig(first, second, third, firstEnabled, enabled, thirdEnabled);
  };
  const handleThirdEnabledChange = (enabled: boolean) => {
    setThirdEnabled(enabled);
    notifyConfig(first, second, third, firstEnabled, secondEnabled, enabled);
  };

  return (
    <div className="rounded-md bg-gray-50 p-4 text-sm">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-gray-700">Inactivar las conversaciones después de</p>
          <input
            type="number"
            value={leadInactivityHours ?? ""}
            min={0}
            disabled={!isEditing}
            placeholder="—"
            onChange={(e) => {
              const v = Number(e.target.value);
              onLeadInactivityHoursChange(
                e.target.value !== "" && Number.isFinite(v) ? v : undefined
              );
            }}
            className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          />
          <p className="text-sm text-gray-700">horas (Por defecto: 48 horas)</p>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2">
            <Switch
              checked={firstEnabled}
              disabled={!isEditing}
              onCheckedChange={handleFirstEnabledChange}
            />
            <p className="text-sm font-medium">1er mensaje de seguimiento</p>
          </div>
          {firstEnabled && (
            <div className="flex flex-col gap-2 pl-6">
              <p className="text-xs text-gray-600">Enviar el mensaje</p>
              <Textarea
                value={first.message}
                disabled={!isEditing}
                onChange={(e) => handleFirstChange({ ...first, message: e.target.value })}
                placeholder="Mensaje de seguimiento"
                rows={3}
                className="text-sm"
              />
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-600">después de</p>
                <input
                  type="number"
                  value={first.minutesAfterInactivity}
                  min={1}
                  disabled={!isEditing}
                  onChange={(e) =>
                    handleFirstChange({
                      ...first,
                      minutesAfterInactivity: Number(e.target.value) || 0,
                    })
                  }
                  className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-gray-600">minutos</p>
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2">
            <Switch
              checked={secondEnabled}
              disabled={!isEditing}
              onCheckedChange={handleSecondEnabledChange}
            />
            <p className="text-sm font-medium">2do mensaje de seguimiento</p>
          </div>
          {secondEnabled && (
            <div className="flex flex-col gap-2 pl-6">
              <p className="text-xs text-gray-600">Enviar el mensaje</p>
              <Textarea
                value={second.message}
                disabled={!isEditing}
                onChange={(e) => handleSecondChange({ ...second, message: e.target.value })}
                placeholder="Mensaje de seguimiento"
                rows={3}
                className="text-sm"
              />
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-600">después de</p>
                <input
                  type="number"
                  value={second.minutesAfterInactivity}
                  min={1}
                  disabled={!isEditing}
                  onChange={(e) =>
                    handleSecondChange({
                      ...second,
                      minutesAfterInactivity: Number(e.target.value) || 0,
                    })
                  }
                  className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-gray-600">minutos</p>
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2">
            <Switch
              checked={thirdEnabled}
              disabled={!isEditing}
              onCheckedChange={handleThirdEnabledChange}
            />
            <p className="text-sm font-medium">3er mensaje de seguimiento</p>
          </div>
          {thirdEnabled && (
            <div className="flex flex-col gap-2 pl-6">
              <p className="text-xs text-gray-600">Enviar el mensaje</p>
              <Textarea
                value={third.message}
                disabled={!isEditing}
                onChange={(e) => handleThirdChange({ ...third, message: e.target.value })}
                placeholder="Mensaje de seguimiento"
                rows={3}
                className="text-sm"
              />
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-600">después de</p>
                <input
                  type="number"
                  value={third.minutesAfterInactivity}
                  min={1}
                  disabled={!isEditing}
                  onChange={(e) =>
                    handleThirdChange({
                      ...third,
                      minutesAfterInactivity: Number(e.target.value) || 0,
                    })
                  }
                  className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-gray-600">minutos</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TEMPERATURE_LEGEND: { value: number; label: string; description: string }[] = [
  {
    value: 0,
    label: "Sin interés / sin respuesta",
    description: "El lead no respondió o no mostró interés.",
  },
  {
    value: 1,
    label: "Solo saludó",
    description: "El lead solo dijo hola, sin proporcionar algún inmueble de interés.",
  },
  {
    value: 2,
    label: "Consulta sobre un inmueble",
    description: "Preguntó por un inmueble específico.",
  },
  {
    value: 3,
    label: "Inmueble no disponible",
    description: "El inmueble consultado ya no está disponible.",
  },
  { value: 4, label: "Inmueble mostrado", description: "Se le mostró información de un inmueble." },
  {
    value: 5,
    label: "Preferencias recolectadas",
    description: "Se capturaron sus preferencias de búsqueda.",
  },
  {
    value: 6,
    label: "Búsqueda sin coincidencias",
    description: "Se buscó pero no hubo resultados que encajen.",
  },
  {
    value: 7,
    label: "Búsqueda con coincidencias",
    description: "Se encontraron inmuebles que encajan con sus preferencias.",
  },
  {
    value: 8,
    label: "Intención de cita",
    description: "El lead expresó intención de agendar una visita.",
  },
];

type DefaultPipelineInactivityFormProps = {
  leadInactivityHours: number | undefined;
  inactivityTransferTemperature: number | undefined;
  onLeadInactivityHoursChange: (value: number | undefined) => void;
  onInactivityTransferTemperatureChange: (value: number | undefined) => void;
  isEditing: boolean;
};

const DefaultPipelineInactivityForm = ({
  leadInactivityHours,
  inactivityTransferTemperature,
  onLeadInactivityHoursChange,
  onInactivityTransferTemperatureChange,
  isEditing,
}: DefaultPipelineInactivityFormProps) => {
  return (
    <div className="rounded-md bg-gray-50 p-4 text-sm">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-gray-700">Inactivar las conversaciones después de</p>
          <input
            type="number"
            value={leadInactivityHours ?? ""}
            min={0}
            disabled={!isEditing}
            placeholder="—"
            onChange={(e) => {
              const v = Number(e.target.value);
              onLeadInactivityHoursChange(
                e.target.value.trim() !== "" && Number.isFinite(v) ? v : undefined
              );
            }}
            className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          />
          <p className="text-sm text-gray-700">horas (Por defecto: 48 horas)</p>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-gray-700">
              Transferir a humano si la temperatura del lead es ≥
            </p>
            <select
              value={inactivityTransferTemperature ?? ""}
              disabled={!isEditing}
              onChange={(e) => {
                const raw = e.target.value;
                onInactivityTransferTemperatureChange(raw === "" ? undefined : Number(raw));
              }}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Desactivado</option>
              {TEMPERATURE_LEGEND.map((row) => (
                <option key={row.value} value={row.value}>
                  {row.value} — {row.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-600">
            Si al momento de inactivar el lead su temperatura es mayor o igual al umbral, se
            transfiere a un humano en lugar de marcarlo como inactivo. Selecciona "Desactivado" para
            apagar esta lógica.
          </p>
          <details className="text-xs text-gray-600">
            <summary className="cursor-pointer text-gray-700 hover:text-gray-900">
              Ver niveles de temperatura
            </summary>
            <ul className="mt-2 flex flex-col gap-1 pl-4">
              {TEMPERATURE_LEGEND.map((row) => (
                <li key={row.value}>
                  <span className="font-mono font-semibold">{row.value}</span>{" "}
                  <span className="font-medium">— {row.label}:</span> {row.description}
                </li>
              ))}
            </ul>
          </details>
        </div>
      </div>
    </div>
  );
};

type EditPosition = {
  id: string;
  displayName: string;
  isSystem?: boolean;
  isNew?: boolean;
  humanAssignmentEnabled?: boolean;
};

type PipelineBlockSavePayload = {
  pipelineId: string;
  displayName?: string;
  leadInactivityHours?: number;
  inactivityTransferTemperature?: number | null;
  followUpConfig?: PipelineFull["followUpConfig"];
  positions: { pending: EditPosition[]; success: EditPosition[]; failure: EditPosition[] };
};

type PipelineBlockProps = {
  pipeline: PipelineFull;
  merchantId: string;
  onSave: (input: PipelineBlockSavePayload) => void;
  isSaving: boolean;
  showTicketPositions?: boolean;
};

const getPositionsForStage = (
  positions: PipelineFull["positions"],
  stage: (typeof STAGE_ORDER)[number]
): PipelinePosition[] => {
  const list = positions[stage];
  return list ?? [];
};

const HUMAN_ASSIGNMENT_TOOLTIP =
  "La asignación y notificación se generá únicamente si el cambio a este estado es generado por el agente IA";

const toEditPositions = (
  positions: PipelineFull["positions"],
  stage: "pending" | "success" | "failure"
): EditPosition[] => {
  const list = positions[stage] ?? [];
  return list.map((p) => ({
    id: p.id,
    displayName: p.displayName,
    isSystem: p.isSystem,
    isNew: false,
    humanAssignmentEnabled:
      (p as { humanAssignmentEnabled?: boolean }).humanAssignmentEnabled ?? false,
  }));
};

const PipelineBlock = ({
  pipeline,
  merchantId: _merchantId,
  onSave,
  isSaving,
  showTicketPositions,
}: PipelineBlockProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState(pipeline.displayName ?? "");
  const [editLeadInactivityHours, setEditLeadInactivityHours] = useState<number | undefined>(
    pipeline.leadInactivityHours
  );
  const [editInactivityTransferTemperature, setEditInactivityTransferTemperature] = useState<
    number | undefined
  >(pipeline.inactivityTransferTemperature);
  const [editFollowUpConfig, setEditFollowUpConfig] = useState<
    NonNullable<PipelineFull["followUpConfig"]>
  >(pipeline.followUpConfig ?? {});
  const [editPositions, setEditPositions] = useState<{
    pending: EditPosition[];
    success: EditPosition[];
    failure: EditPosition[];
  }>(() => ({
    pending: toEditPositions(pipeline.positions, "pending"),
    success: toEditPositions(pipeline.positions, "success"),
    failure: toEditPositions(pipeline.positions, "failure"),
  }));

  useEffect(() => {
    setEditDisplayName(pipeline.displayName ?? "");
    setEditLeadInactivityHours(pipeline.leadInactivityHours);
    setEditInactivityTransferTemperature(pipeline.inactivityTransferTemperature);
    setEditFollowUpConfig(pipeline.followUpConfig ?? {});
    setEditPositions({
      pending: toEditPositions(pipeline.positions, "pending"),
      success: toEditPositions(pipeline.positions, "success"),
      failure: toEditPositions(pipeline.positions, "failure"),
    });
  }, [
    pipeline.displayName,
    pipeline.leadInactivityHours,
    pipeline.inactivityTransferTemperature,
    pipeline.followUpConfig,
    pipeline.positions,
  ]);

  const handleStartEdit = () => {
    setEditDisplayName(pipeline.displayName ?? "");
    setEditLeadInactivityHours(pipeline.leadInactivityHours);
    setEditInactivityTransferTemperature(pipeline.inactivityTransferTemperature);
    setEditFollowUpConfig(pipeline.followUpConfig ?? {});
    setEditPositions({
      pending: toEditPositions(pipeline.positions, "pending"),
      success: toEditPositions(pipeline.positions, "success"),
      failure: toEditPositions(pipeline.positions, "failure"),
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditDisplayName(pipeline.displayName ?? "");
    setEditLeadInactivityHours(pipeline.leadInactivityHours);
    setEditInactivityTransferTemperature(pipeline.inactivityTransferTemperature);
    setEditFollowUpConfig(pipeline.followUpConfig ?? {});
    setEditPositions({
      pending: toEditPositions(pipeline.positions, "pending"),
      success: toEditPositions(pipeline.positions, "success"),
      failure: toEditPositions(pipeline.positions, "failure"),
    });
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    onSave({
      pipelineId: pipeline.id,
      displayName: editDisplayName.trim() || undefined,
      leadInactivityHours: editLeadInactivityHours,
      inactivityTransferTemperature:
        pipeline.id === "default" ? editInactivityTransferTemperature ?? null : undefined,
      followUpConfig: pipeline.id === "default" ? undefined : editFollowUpConfig,
      positions: editPositions,
    });
    setIsEditing(false);
  };

  const addPositionToStage = (stage: "pending" | "success" | "failure") => {
    setEditPositions((prev) => ({
      ...prev,
      [stage]: [
        ...prev[stage],
        { id: "", displayName: "", isNew: true, humanAssignmentEnabled: false },
      ],
    }));
  };

  const removePositionFromStage = (stage: "pending" | "success" | "failure", index: number) => {
    setEditPositions((prev) => ({
      ...prev,
      [stage]: prev[stage].filter((_, i) => i !== index),
    }));
  };

  const updatePositionInStage = (
    stage: "pending" | "success" | "failure",
    index: number,
    field: "id" | "displayName" | "humanAssignmentEnabled",
    value: string | boolean
  ) => {
    setEditPositions((prev) => {
      const list = [...prev[stage]];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [stage]: list };
    });
  };

  const isDefaultPipeline = pipeline.id === "default";

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:p-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {isEditing ? (
              <>
                <Input
                  className="h-8 max-w-[240px] text-sm"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  placeholder={pipeline.id}
                />
                <p className="text-md font-semibold text-gray-700">({pipeline.id})</p>
              </>
            ) : (
              <p className="text-md font-semibold text-gray-700">
                {pipeline.displayName || pipeline.id} ({pipeline.id})
              </p>
            )}
          </div>
          {isEditing ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={handleSaveEdit}
                disabled={isSaving}
              >
                {isSaving && <Loader2 className="size-3 animate-spin" />}
                Guardar
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                Cancelar
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={handleStartEdit}>
              <Edit2 className="size-3" />
              Editar
            </Button>
          )}
        </div>

        <p className="mt-2 text-sm font-semibold text-gray-700">Etapas y posiciones</p>
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {STAGE_ORDER.map((stage) => {
              const canAdd = stage === "pending" || stage === "success" || stage === "failure";
              const isEditStage = canAdd && isEditing;
              const positions = isEditStage
                ? editPositions[stage]
                : getPositionsForStage(pipeline.positions, stage);
              return (
                <div key={stage} className="flex flex-col gap-2">
                  <p className="text-sm font-semibold text-gray-700">{STAGE_LABELS[stage]}</p>
                  {isEditStage
                    ? (editPositions[stage] as EditPosition[]).map((pos, index) => (
                        <EditPositionRow
                          key={pos.isNew ? `new-${index}` : pos.id}
                          stage={stage}
                          id={pos.id}
                          displayName={pos.displayName}
                          isSystem={pos.isSystem}
                          humanAssignmentEnabled={pos.humanAssignmentEnabled}
                          onIdChange={(value) => updatePositionInStage(stage, index, "id", value)}
                          onDisplayNameChange={(value) =>
                            updatePositionInStage(stage, index, "displayName", value)
                          }
                          onHumanAssignmentChange={(value) =>
                            updatePositionInStage(stage, index, "humanAssignmentEnabled", value)
                          }
                          onRemove={() => removePositionFromStage(stage, index)}
                          canEdit={!pos.isSystem}
                        />
                      ))
                    : (positions as PipelinePosition[]).map((pos) => (
                        <PositionCard
                          key={pos.id}
                          position={pos}
                          pipelineId={pipeline.id}
                          stage={stage}
                          onRemove={() => {}}
                          onRename={() => {}}
                          canEdit={false}
                        />
                      ))}
                  {canAdd && isEditing && (
                    <AddPositionButton
                      stage={stage}
                      onClick={() => addPositionToStage(stage)}
                      isDisabled={false}
                      isLoading={false}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {isDefaultPipeline ? (
          <div>
            <p className="mb-2 text-sm font-semibold text-gray-700">Inactividad y transferencia</p>
            <DefaultPipelineInactivityForm
              leadInactivityHours={editLeadInactivityHours}
              inactivityTransferTemperature={editInactivityTransferTemperature}
              onLeadInactivityHoursChange={setEditLeadInactivityHours}
              onInactivityTransferTemperatureChange={setEditInactivityTransferTemperature}
              isEditing={isEditing}
            />
          </div>
        ) : (
          <div>
            <p className="mb-2 text-sm font-semibold text-gray-700">
              Mensajes de Seguimiento e inactividad
            </p>
            <FollowUpConfigForm
              leadInactivityHours={editLeadInactivityHours}
              followUpConfig={editFollowUpConfig}
              onLeadInactivityHoursChange={setEditLeadInactivityHours}
              onFollowUpConfigChange={setEditFollowUpConfig}
              isEditing={isEditing}
            />
          </div>
        )}

        {showTicketPositions && (
          <TicketPositionBlock pipelineId={pipeline.id} />
        )}
      </div>
    </div>
  );
};

type PositionCardProps = {
  position: PipelinePosition;
  pipelineId: string;
  stage: string;
  onRemove: (input: { pipelineId: string; positionId: string }) => void;
  onRename: (input: { pipelineId: string; positionId: string; displayName: string }) => void;
  canEdit: boolean;
};

const PositionCard = ({ position, pipelineId, onRemove, onRename, canEdit }: PositionCardProps) => {
  const [displayName, setDisplayName] = useState(position.displayName);
  useEffect(() => {
    setDisplayName(position.displayName);
  }, [position.displayName]);

  const handleBlur = () => {
    if (displayName.trim() !== position.displayName) {
      onRename({ pipelineId, positionId: position.id, displayName: displayName.trim() });
    }
  };

  return (
    <div
      className={cn(
        "rounded-md border bg-white p-2",
        position.isSystem ? "border-gray-300" : "border-blue-200"
      )}
    >
      <div className="flex flex-col gap-1">
        <p className="font-mono text-xs text-gray-600">{position.id}</p>
        {canEdit ? (
          <>
            <Input
              className="h-6 px-1 text-xs"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onBlur={handleBlur}
              placeholder="Nombre"
            />
            <Button
              size="sm"
              variant="ghost"
              className="h-6 justify-start px-1 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => onRemove({ pipelineId, positionId: position.id })}
            >
              <Trash2 className="size-3" />
              Quitar
            </Button>
          </>
        ) : (
          <p className="text-sm font-medium text-gray-700">{position.displayName}</p>
        )}
        {!position.isSystem && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <label className="flex items-center gap-1.5">
                    <Checkbox
                      checked={
                        (position as { humanAssignmentEnabled?: boolean }).humanAssignmentEnabled ??
                        false
                      }
                      disabled
                    />
                    <span className="text-xs">Asignación agente humano</span>
                  </label>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[280px]">{HUMAN_ASSIGNMENT_TOOLTIP}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};

type EditPositionRowProps = {
  stage: "pending" | "success" | "failure";
  id: string;
  displayName: string;
  isSystem?: boolean;
  humanAssignmentEnabled?: boolean;
  onIdChange: (value: string) => void;
  onDisplayNameChange: (value: string) => void;
  onHumanAssignmentChange?: (value: boolean) => void;
  onRemove: () => void;
  canEdit: boolean;
};

const getEditPositionRowBorderColor = (stage: "pending" | "success" | "failure"): string => {
  if (stage === "pending") return "border-blue-200";
  if (stage === "success") return "border-green-200";
  return "border-red-200";
};

const EditPositionRow = ({
  stage,
  id,
  displayName,
  isSystem,
  humanAssignmentEnabled,
  onIdChange,
  onDisplayNameChange,
  onHumanAssignmentChange,
  onRemove,
  canEdit,
}: EditPositionRowProps) => (
  <div className={cn("rounded-md border bg-white p-2", getEditPositionRowBorderColor(stage))}>
    <div className="flex flex-col gap-1">
      {canEdit ? (
        <Input
          placeholder="id (ej: enRevision)"
          value={id}
          className="h-6 px-1 text-xs"
          onChange={(e) =>
            onIdChange(e.target.value.replace(/\s/g, "").replace(/([a-z])([A-Z])/g, "$1$2"))
          }
        />
      ) : (
        <p className="font-mono text-xs text-gray-600">{id}</p>
      )}
      {canEdit ? (
        <Input
          placeholder="Nombre (ej: En revisión)"
          value={displayName}
          className="h-6 px-1 text-xs"
          onChange={(e) => onDisplayNameChange(e.target.value)}
        />
      ) : (
        <p className="text-sm font-medium text-gray-700">{displayName}</p>
      )}
      {!isSystem && onHumanAssignmentChange && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block">
                <label className="flex items-center gap-1.5">
                  <Checkbox
                    checked={humanAssignmentEnabled}
                    disabled={!canEdit}
                    onCheckedChange={(checked) => onHumanAssignmentChange(!!checked)}
                  />
                  <span className="text-xs">Asignación agente humano</span>
                </label>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px]">{HUMAN_ASSIGNMENT_TOOLTIP}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {canEdit && !isSystem && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 justify-start px-1 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={onRemove}
        >
          <Trash2 className="size-3" />
          Quitar
        </Button>
      )}
    </div>
  </div>
);

type AddPositionButtonProps = {
  stage: "pending" | "success" | "failure";
  onClick: () => void;
  isDisabled: boolean;
  isLoading: boolean;
};

const getAddPositionButtonClasses = (stage: "pending" | "success" | "failure"): string => {
  if (stage === "pending") return "border-blue-300 text-blue-700 hover:bg-blue-50";
  if (stage === "success") return "border-green-300 text-green-700 hover:bg-green-50";
  return "border-red-300 text-red-700 hover:bg-red-50";
};

const AddPositionButton = ({ stage, onClick, isDisabled, isLoading }: AddPositionButtonProps) => (
  <Button
    size="sm"
    variant="outline"
    className={cn("w-full", getAddPositionButtonClasses(stage))}
    onClick={onClick}
    disabled={isDisabled}
  >
    {isLoading ? <Loader2 className="size-3 animate-spin" /> : <Plus className="size-3" />}
    Añadir posición
  </Button>
);

export type { PipelineBlockSavePayload, EditPosition };
export default PipelineBlock;
